import {
  createContext,
  MutableRefObject,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Client,
  Conversation,
  Message,
  Paginator,
} from "@twilio/conversations";
import { CurrentActor, useCurrentActor, useCurrentUser } from "./AuthContext";
import {
  getContacts,
  getConversationClientToken,
  getConversations,
} from "../api/endpoints/chat/conversations";
import {
  ChatMessage,
  ChatMessagesGroup,
} from "../components/chat_v2/ChatMessagesList";
import useStateWithRef from "../hooks/useStateWithRef";
import ConversationModel, {
  ChatContact,
} from "../api/models/ConversationModel";
import ConversationContactModel from "../api/models/ConversationContactModel";
import { ApiResponse } from "../api/apiResponse";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import { useMessages } from "../hooks/useMessages";

type OnInitialized = () => void | undefined;
type OnMessageAdded = (message: Message) => void | undefined;
export type ChatContext = {
  isInitialized: boolean;
  getConversation: (sid: string) => Promise<Conversation>;
  initializeChat: (
    onInitialized: OnInitialized,
    onMessageAdded: OnMessageAdded
  ) => void;
  getMessagesPage: (conv: Conversation) => Promise<Paginator<Message>>;
  getConversationMessages: (convSid: string) => Promise<ChatMessage[]>;
  getUnreadCount: (convSid: string) => Promise<number>;
  setSelectedConversation: (conv: Conversation) => void;
  selectedConversation: Conversation | undefined;
  selectedConversationRef: MutableRefObject<Conversation | undefined>;
  currentActorTwillioIdentity: string;
  conversations: ConversationModel[];
  contacts: ConversationContactModel[];
  messagesRef: MutableRefObject<ChatMessage[]>;
  messages: ChatMessage[];
  groupedMessages: ChatMessagesGroup;
  setMessages: (m: ChatMessage[]) => void;
  isLoadingMessages: boolean;
  setIsLoadingMessages: (v: boolean) => void;
  refreshConversations: () => void;
  refreshContacts: () => void;
  conversationContacts: ChatContact[];
  newContacts: ChatContact[];
  filteredConversationContacts: ChatContact[];
  filteredNewContacts: ChatContact[];
  search: (val: string) => void;
  resetSearchResults: () => void;
  currentActor: CurrentActor;
  prependConversation: (conversation: ConversationModel) => void;
  unreadMessagesCounter: number;
  resetMessages: () => void;
  scrollContactCardIntoView: (twilioId: string) => void;
};

export const ChatContext = createContext<ChatContext | null>(null);

let conversations_cache: Record<string, Conversation> = {};
export function ChatContextProvider({ children }: { children: ReactNode }) {
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [conversations, setConversations] = useState<ConversationModel[]>([]);
  const [unreadMessagesCounter, setUnreadMessagesCounter] = useState<number>(0);
  const [contacts, setContacts] = useState<ConversationContactModel[]>([]);
  const clientRef = useRef<Client | undefined>();
  const [messagesRef, messages, groupedMessages, setMessages] = useMessages();
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const [
    selectedConversationRef,
    selectedConversation,
    setSelectedConversation,
  ] = useStateWithRef<Conversation | undefined>(undefined);
  const [filteredConversationContacts, setFilteredConversationContacts] =
    useState<ChatContact[]>([]);
  const [filteredNewContacts, setFilteredNewContacts] = useState<ChatContact[]>(
    []
  );

  let currentToken: string | undefined;

  const currentActor = useCurrentActor();
  const currentUser = useCurrentUser();
  const currentActorTwillioIdentity: string = currentUser!.twilioIdentity!;

  const conversationContacts = ConversationModel.toChatContacts(
    conversations,
    currentActorTwillioIdentity
  );
  const newContacts = ConversationContactModel.toChatContacts(contacts);

  const initialize = async (
    onInitialized: OnInitialized,
    onMessageAdded: OnMessageAdded
  ) => {
    if (!currentToken) {
      const tokenResponse = await getConversationClientToken(currentActor);
      if (!tokenResponse.isSuccess)
        throw new Error("Unable to fetch chat token");
      currentToken = tokenResponse.data.token;
    }
    if (!clientRef.current) {
      const newClient = new Client(currentToken!, { logLevel: "error" });
      clientRef.current = newClient;
      newClient.once("initialized", () => {
        setIsInitialized(true);
        if (!!onInitialized) onInitialized();
      });
      if (!!onMessageAdded)
        newClient.addListener("messageAdded", onMessageAdded);
    }
  };

  const getTwilioConversation = async (sid: string): Promise<Conversation> => {
    if (!!conversations_cache[sid]) return conversations_cache[sid];
    const conversation = await clientRef.current!.getConversationBySid(sid);
    conversations_cache[sid] = conversation;
    return conversation;
  };

  const getMessagesPage = (
    conversation: Conversation
  ): Promise<Paginator<Message>> => {
    return conversation.getMessages(300);
  };

  const getConversationMessages = async (
    conversationId: string
  ): Promise<ChatMessage[]> => {
    const conversation: Conversation = await getTwilioConversation(
      conversationId
    );
    const messagesPage: Paginator<Message> = await getMessagesPage(
      conversation
    );
    const fetchedMessages: ChatMessage[] = messagesPage.items.map((message) => {
      return {
        message: message.body ?? "No Message",
        direction:
          message.author === currentActorTwillioIdentity ? "sent" : "received",
        date: message.dateCreated ?? moment().toDate(),
      };
    });
    return fetchedMessages;
  };

  const getUnreadCount = async (conversationId: string) => {
    const conversation: Conversation = await getTwilioConversation(
      conversationId
    );
    const count = (await conversation.getUnreadMessagesCount()) || 0;
    return count;
  };

  const refreshConversations = async () => {
    const response: ApiResponse = await getConversations(currentActor);
    if (response.isSuccess) {
      const fetchedConversations: ConversationModel[] =
        ConversationModel.collectionFromApiResponse(response.data);
      const unreadCounts = await Promise.all(
        fetchedConversations.map(async (con) => {
          const count = await getUnreadCount(con.twilioId);
          con.incrementUnreadCount(count);
          return count;
        })
      );
      setUnreadMessagesCounter(unreadCounts.reduce((a, v) => a + v, 0));
      fetchedConversations.sort((a, b) =>
        b.lastMessageSentAt.diff(a.lastMessageSentAt)
      );
      setConversations(fetchedConversations);
    } else {
      toast.error(response.errorMessages.join(", "));
    }
  };

  const refreshConversationsUnreadCount = async () => {
    if (conversations.length === 0) return;
    const convs = [...conversations];
    await Promise.all(
      convs.map(async (con) => {
        const count = await getUnreadCount(con.twilioId);
        con.incrementUnreadCount(count);
        return count;
      })
    );
    setConversations(convs);
  };

  const refreshContacts = async () => {
    const response: ApiResponse = await getContacts(currentActor);
    if (response.isSuccess) {
      const fetchedContacts =
        ConversationContactModel.collectionFromApiResponse(response.data);
      setContacts(fetchedContacts);
    } else {
      toast.error(response.errorMessages.join(", "));
    }
  };

  const search = (val: string) => {
    const filteredConversations = conversationContacts.filter((contact) =>
      contact.name.toLowerCase().includes(val.toLowerCase())
    );
    const filteredContacts = newContacts.filter((contact) =>
      contact.name.toLowerCase().includes(val.toLowerCase())
    );
    setFilteredConversationContacts(filteredConversations);
    setFilteredNewContacts(filteredContacts);
  };

  const prependConversation = (conversation: ConversationModel) => {
    setConversations([conversation, ...conversations]);
  };

  const resetSearchResults = () => {
    setFilteredConversationContacts([]);
    setFilteredNewContacts([]);
  };

  const resetMessages = () => {
    setMessages([]);
  };

  const scrollContactCardIntoView = (twilioId: string) => {
    const chatCard = document.getElementById(twilioId);
    if (!!chatCard) {
      chatCard.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "center",
      });
    }
  };

  const authContext: ChatContext = {
    isInitialized,
    initializeChat: initialize,
    getConversation: getTwilioConversation,
    getMessagesPage,
    getConversationMessages,
    getUnreadCount,
    setSelectedConversation,
    selectedConversation,
    selectedConversationRef,
    currentActorTwillioIdentity,
    messages,
    groupedMessages,
    messagesRef,
    setMessages,
    isLoadingMessages,
    setIsLoadingMessages,
    contacts,
    conversations,
    refreshContacts,
    refreshConversations,
    conversationContacts,
    newContacts,
    filteredNewContacts,
    filteredConversationContacts,
    search,
    resetSearchResults,
    currentActor,
    prependConversation,
    unreadMessagesCounter,
    resetMessages,
    scrollContactCardIntoView,
  };

  return (
    <ChatContext.Provider value={authContext}>{children}</ChatContext.Provider>
  );
}

export function useChat(): ChatContext {
  const context = useContext(ChatContext);
  if (!context) throw new Error("Chat can't be used");

  return context;
}
